import React, { memo, useRef, useState, useEffect, useCallback } from 'react'
import { m } from 'framer-motion'
import { Box, Card } from '@mui/material'

import { getCountByValue } from '../../util/game.helpers'
import CoinGrid from './CoinGrid'
import Button from '../shared/button/Button'
import scratchNew from 'assets/scratch-new.jpg'

const RevealGrid = ({ value, onCompleteScratch, t }) => {
  const canvasRef = useRef(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [isStartedScratch, setIsStartedScratch] = useState(false)
  const [scratched, setScratched] = useState(false)

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const parent = canvas.parentElement
    canvas.width = parent.clientWidth
    canvas.height = parent.clientHeight
    drawScratchLayer()
  }, [])

  useEffect(() => {
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)
    return () => window.removeEventListener('resize', resizeCanvas)
  }, [resizeCanvas])

  const drawScratchLayer = () => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const img = new Image()
    img.src = scratchNew
    img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
  }

  const getPosition = (e) => {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    return e.touches
      ? {
          x: e.touches[0].clientX - rect.left,
          y: e.touches[0].clientY - rect.top
        }
      : { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  useEffect(() => {
    const preventScroll = (e) => e.preventDefault()
    document.addEventListener('touchmove', preventScroll, { passive: false })
    return () => document.removeEventListener('touchmove', preventScroll)
  }, [])

  const startScratching = (e) => {
    e.preventDefault()
    setIsDrawing(true)
    setIsStartedScratch(true)
    scratch(e)
  }

  const scratch = (e) => {
    if (!isDrawing) return
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const { x, y } = getPosition(e)

    ctx.globalCompositeOperation = 'destination-out'
    ctx.beginPath()
    ctx.arc(x, y, 25, 0, Math.PI * 2)
    ctx.fill()

    checkScratchCompletion()
  }

  const stopScratching = (e) => {
    e.preventDefault()
    setIsDrawing(false)
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    ctx.globalCompositeOperation = 'destination-out'
  }

  const checkScratchCompletion = () => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data
    const scratchedRatio =
      Array.from(
        { length: pixels.length / 4 },
        (_, i) => pixels[i * 4 + 3] === 0
      ).filter(Boolean).length /
      (canvas.width * canvas.height)
    if (scratchedRatio >= 0.5) handleCompleteScratch()
  }

  const handleCompleteScratch = () => {
    setScratched(true)
    onCompleteScratch()
  }

  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      gap={3}
      sx={{ width: '100%', aspectRatio: '1/1' }}
    >
      <m.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        style={{ width: '100%', height: '100%' }}
      >
        <Card
          elevation={0}
          sx={{
            width: '100%',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            aspectRatio: '1/1'
          }}
        >
          {(isStartedScratch || scratched) && (
            <CoinGrid value={getCountByValue(value)} />
          )}
          {!scratched && (
            <canvas
              ref={canvasRef}
              onMouseDown={startScratching}
              onMouseMove={scratch}
              onMouseUp={stopScratching}
              onMouseLeave={stopScratching}
              onTouchStart={startScratching}
              onTouchMove={scratch}
              onTouchEnd={stopScratching}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                touchAction: 'none'
              }}
            />
          )}
        </Card>
      </m.div>
      {!scratched && (
        <Button
          label={t('game.scratch-fully')}
          onClick={handleCompleteScratch}
        />
      )}
    </Box>
  )
}

export default memo(RevealGrid)
